import React, { useMemo } from 'react';
import { Alert, AlertTitle, Typography, Chip } from '@mui/material';
import {
    PriorityHigh as PriorityHighIcon,
    Warning as WarningIcon,
    AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useNotifications } from '../context/NotificationContext';
import { NOTIFICATION_EVENTS, isOverdueEvent, isReminderEvent } from '../constants';

/**
 * Get banner severity and icon based on notification type
 */
const getBannerProps = (eventKey) => {
    switch (eventKey) {
        case NOTIFICATION_EVENTS.INTERNAL_ESCALATION:
        case NOTIFICATION_EVENTS.EXTERNAL_ESCALATION:
            return {
                severity: 'error',
                icon: <PriorityHighIcon />,
                title: 'Needs Attention',
            };
        case NOTIFICATION_EVENTS.DUEDATE_REMINDER:
            return {
                severity: 'warning',
                icon: <WarningIcon />,
                title: 'Due Date Approaching',
            };
        default:
            return {
                severity: 'info',
                icon: <AccessTimeIcon />,
                title: 'Attention Required',
            };
    }
};

/**
 * Generate banner message based on notification
 */
const generateBannerMessage = (notification) => {
    const { eventKey, entityTitle, entityType } = notification;
    const title = entityTitle;
    const entityTypeName =
        entityType === 'DEFICIENCY' ? 'deficiency' : entityType?.toLowerCase() || 'item';

    switch (eventKey) {
        case NOTIFICATION_EVENTS.INTERNAL_ESCALATION:
            return `${entityTypeName.charAt(0).toUpperCase() + entityTypeName.slice(1)} '${title}' is overdue`;
        case NOTIFICATION_EVENTS.EXTERNAL_ESCALATION:
            return `${entityTypeName.charAt(0).toUpperCase() + entityTypeName.slice(1)} '${title}' is overdue.`;
        case NOTIFICATION_EVENTS.DUEDATE_REMINDER:
            return `${entityTypeName.charAt(0).toUpperCase() + entityTypeName.slice(1)} '${title}' is due soon. Please ensure work is completed on time.`;
        default:
            return `${entityTypeName.charAt(0).toUpperCase() + entityTypeName.slice(1)} '${title}' requires your attention.`;
    }
};

/**
 * NotificationBanner Component
 * Displays priority notifications (escalations and due date reminders) as alert banners.
 * Used in the deficiency sidebar to show priority status of the current deficiency.
 */
const NotificationBanner = ({ deficiencyId, dueDateState, variant = 'sidebar' }) => {
    const { notifications } = useNotifications();

    // Filter notifications for this specific deficiency
    const relevantNotifications = useMemo(() => {
        if (!notifications || !Array.isArray(notifications)) {
            return [];
        }

        // Filter by priority events and specific deficiency
        return notifications
            .filter(
                (n) =>
                    (isOverdueEvent(n.eventKey) || isReminderEvent(n.eventKey)) &&
                    n.entityType === 'DEFICIENCY' &&
                    n.entityId === deficiencyId
            )
            .sort((a, b) => {
                // Escalations take priority over reminders
                if (isOverdueEvent(a.eventKey) && !isOverdueEvent(b.eventKey)) return -1;
                if (!isOverdueEvent(a.eventKey) && isOverdueEvent(b.eventKey)) return 1;
                return 0;
            });
    }, [notifications, deficiencyId]);

    const fallbackEventKey =
        dueDateState === 'OVERDUE'
            ? NOTIFICATION_EVENTS.EXTERNAL_ESCALATION
            : dueDateState === 'DUE_SOON'
              ? NOTIFICATION_EVENTS.DUEDATE_REMINDER
              : null;

    if ((!relevantNotifications || relevantNotifications.length === 0) && !fallbackEventKey) {
        return null;
    }

    // Just show the highest priority notification
    const notification = relevantNotifications?.[0] || null;
    const eventKey = notification?.eventKey || fallbackEventKey;
    const bannerProps = getBannerProps(eventKey);
    const message = notification
        ? generateBannerMessage(notification)
        : eventKey === NOTIFICATION_EVENTS.DUEDATE_REMINDER
          ? 'Deficiency is due soon. Please ensure work is completed on time.'
          : 'Deficiency is overdue.';

    return (
        <Alert
            severity={bannerProps.severity}
            icon={bannerProps.icon}
            sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                mb: 2,
                '& .MuiAlert-message': {
                    width: '100%',
                    padding: 0,
                },
                fontSize: '0.875rem',
                '& .MuiAlert-icon': {
                    fontSize: '1.2rem',
                },
            }}
        >
            <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
                {bannerProps.title}
                <Chip
                    label="Priority"
                    size="small"
                    color={bannerProps.severity}
                    sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                />
            </AlertTitle>
            <Typography variant="body2">{message}</Typography>
        </Alert>
    );
};

export default NotificationBanner;
